// "New feature launch" — a left sidebar (mirrors the New Release drawer)
// with two inputs: pick the gating flag(s) from a searchable list (like the
// uncurated-GitHub-releases list), and paste freeform notes. On create we
// draft the feature from the flag, run the LLM autofill over the notes, then
// drop the editor straight onto the feature page to review the result.
//
// Deliberately NOT a multi-step form: the old wizard made people hand-fill
// every field. Here they write once, we extract, they refine on the page.
import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import type { FeatureFlag } from "@release-hub/shared";
import { ApiError } from "@/api/client";
import { useCreateFeatureLaunch } from "@/api/mutations";
import { useFeatures, useFlags } from "@/api/queries";
import { SideDrawer } from "@/components/SideDrawer";
import { Button } from "@/ui/button";
import { cn } from "@/lib/cn";

/** Server's flag-id → title de-slug, mirrored so the seed name matches. */
function nameFromFlag(fid: string): string {
  return fid.replace(/[_\-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function NewFeatureLaunchDrawer() {
  return (
    <SideDrawer
      width="max-w-lg"
      title="New feature launch"
      description="Pick the flag this launch is gated by, paste any notes, and we'll draft the rest — then you land on the feature page to review."
      trigger={
        <button
          type="button"
          className="rounded-md border border-border-hover bg-card px-3 py-1.5 text-sm text-text-secondary hover:border-text/40 hover:text-text"
        >
          + New feature launch
        </button>
      }
    >
      <NewFeatureLaunchBody />
    </SideDrawer>
  );
}

function NewFeatureLaunchBody() {
  const [, navigate] = useLocation();
  const { data: allFlags, isPending } = useFlags();
  const { data: features } = useFeatures();
  const create = useCreateFeatureLaunch();

  const [flags, setFlags] = useState<string[]>([]);
  const [notes, setNotes] = useState("");
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");

  const busy = create.isPending;

  const selectedSet = useMemo(() => new Set(flags), [flags]);

  // Flags not yet picked, filtered by the search box. Selected flags render
  // in their own pinned section above so the anchor stays visible.
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (allFlags ?? [])
      .filter((f) => !selectedSet.has(f.id))
      .filter(
        (f) =>
          !q ||
          f.id.toLowerCase().includes(q) ||
          (f.description ?? "").toLowerCase().includes(q),
      )
      .slice(0, 100);
  }, [allFlags, query, selectedSet]);

  const selectedFlags = useMemo(
    () =>
      flags
        .map((id) => (allFlags ?? []).find((f) => f.id === id))
        .filter((f): f is FeatureFlag => !!f),
    [flags, allFlags],
  );

  // Soft duplicate guard — a feature already claiming any selected flag.
  const duplicate = useMemo(() => {
    if (flags.length === 0) return null;
    return (
      (features ?? []).find((f) => f.flag_ids.some((id) => selectedSet.has(id))) ??
      null
    );
  }, [flags, features, selectedSet]);

  const toggle = (id: string) =>
    setFlags((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const submit = async () => {
    if (flags.length === 0 || busy) return;
    setError("");
    try {
      // Create the draft and land on its page immediately. If notes were
      // pasted, the mutation marks it seeding and fires the LLM field-seed in
      // the background — the detail page shows a seeding skeleton until it
      // clears, so nobody starts authoring over fields about to be filled.
      const { id } = await create.mutateAsync({
        flag_ids: flags,
        name: nameFromFlag(flags[0]!),
        blurb: notes.trim(),
      });
      navigate(`/features/${id}`);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.detail
          : e instanceof Error
            ? e.message
            : String(e),
      );
    }
  };

  return (
    <div className="space-y-5">
      {/* ── Flags ─────────────────────────────────────────────────── */}
      <section className="space-y-2">
        <div className="flex items-baseline justify-between">
          <h3 className="text-sm font-medium">Gating flag(s)</h3>
          {flags.length > 0 && (
            <span className="text-xs text-text-tertiary">
              {flags.length} selected · first is primary
            </span>
          )}
        </div>

        {selectedFlags.length > 0 && (
          <ul className="space-y-1">
            {selectedFlags.map((f, i) => (
              <FlagRow
                key={f.id}
                flag={f}
                checked
                primary={i === 0}
                onToggle={() => toggle(f.id)}
              />
            ))}
          </ul>
        )}

        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search flags by id or description…"
          className="w-full rounded-md border border-border bg-bg px-3 py-1.5 text-sm focus:border-text/40 focus:outline-none"
        />

        <div className="max-h-[50vh] space-y-1 overflow-y-auto rounded-md border border-border p-1">
          {isPending ? (
            <p className="px-2 py-6 text-center text-xs text-text-tertiary">
              Loading flags…
            </p>
          ) : matches.length === 0 ? (
            <p className="px-2 py-6 text-center text-xs text-text-tertiary">
              {query ? "No flags match." : "No more flags."}
            </p>
          ) : (
            <ul className="space-y-1">
              {matches.map((f) => (
                <FlagRow key={f.id} flag={f} onToggle={() => toggle(f.id)} />
              ))}
            </ul>
          )}
        </div>

        {duplicate && (
          <p className="rounded border border-amber-400/50 bg-amber-50/60 px-3 py-2 text-xs text-amber-900">
            <strong>{duplicate.name}</strong> already tracks one of these flags.{" "}
            <button
              type="button"
              className="underline"
              onClick={() => navigate(`/features/${duplicate.id}`)}
            >
              Open it instead
            </button>{" "}
            — or continue to create a separate launch.
          </p>
        )}
      </section>

      {/* ── Notes ─────────────────────────────────────────────────── */}
      <section className="space-y-2">
        <h3 className="text-sm font-medium">Notes to draft from</h3>
        <p className="text-xs text-text-tertiary">
          Paste a Slack blurb, PRD snippet, or rough notes — freeform is fine.
          We pull out these fields, so include whatever you can:
        </p>
        <ul className="space-y-0.5 text-xs text-text-tertiary">
          <li>
            <span className="text-text-secondary">What it is</span> — a one-line
            pitch plus a fuller customer-facing description
          </li>
          <li>
            <span className="text-text-secondary">Who it's for</span> — the
            audience (customer segment or role)
          </li>
          <li>
            <span className="text-text-secondary">Product area</span> — the
            feature family it belongs to
          </li>
          <li>
            <span className="text-text-secondary">Access</span> — EA or GA
          </li>
          <li>
            <span className="text-text-secondary">Dates</span> — EA date and GA
            date (an exact date, or a target quarter / week / TBD)
          </li>
          <li>
            <span className="text-text-secondary">Platforms</span> — web and/or
            mobile, plus any platform-specific notes
          </li>
          <li>
            <span className="text-text-secondary">Readiness</span> — beta or
            stable
          </li>
          <li>
            <span className="text-text-secondary">Known gaps</span> — bugs or
            missing pieces shipping with it
          </li>
        </ul>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={7}
          placeholder={
            "e.g. Procedure Folders lets teams organize procedures into nested folders.\n" +
            "EA'd to 3 design partners in Aug 2026, GA targeting Q3 2026.\n" +
            "Web + mobile. Beta. Known gap: no bulk move yet."
          }
          className="w-full resize-y rounded-md border border-border bg-bg px-3 py-2 text-sm text-text focus:border-text/40 focus:outline-none"
        />
      </section>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex items-center justify-between border-t border-border pt-3">
        <span className="text-xs text-text-tertiary">
          Draft only — nothing is public until you publish.
        </span>
        <Button onClick={submit} loading={busy} disabled={flags.length === 0}>
          {create.isPending ? "Creating…" : "Create draft"}
        </Button>
      </div>
    </div>
  );
}

function FlagRow({
  flag,
  checked = false,
  primary = false,
  onToggle,
}: {
  flag: FeatureFlag;
  checked?: boolean;
  primary?: boolean;
  onToggle: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          "flex w-full items-start gap-2.5 rounded px-2 py-1.5 text-left transition-colors",
          checked ? "bg-accent/5" : "hover:bg-border/50",
        )}
      >
        <span
          className={cn(
            "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px] leading-none",
            checked ? "border-text bg-text text-bg" : "border-border-hover",
          )}
        >
          {checked && "✓"}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="truncate font-mono text-[12px] text-text">
              {flag.id}
            </span>
            {primary && (
              <span className="rounded-full bg-border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-text-secondary">
                Primary
              </span>
            )}
            {flag.platforms.length > 0 && (
              <span className="shrink-0 text-[10px] uppercase tracking-wider text-text-tertiary">
                {flag.platforms.join(" · ")}
              </span>
            )}
          </span>
          {flag.description && (
            <span className="mt-0.5 block truncate text-xs text-text-tertiary">
              {flag.description}
            </span>
          )}
        </span>
      </button>
    </li>
  );
}
